/* eslint-disable import/no-webpack-loader-syntax */
import {
  useRef,
  useState,
  useMemo,
  useEffect,
  useCallback,
  useContext,
  createContext
} from "react";

import useNotifications from "../useNotifications";
import useTimeout from "../useTimeout";
import { playSound } from "../../utils/audio";
import { useDataSettings } from "../useData";
import useSession from "../useSession";
import { useLocation } from "react-router-dom";
import { PromiseWithResolvers } from "../../utils/promises";
import ConfirmGeneric from "./confirmGeneric";
import ConfirmDelete from "./confirmDelete";
import ConfirmUnsavedChanges from "./confirmUnsavedChanges";
import HideScreen from "./hideScreen";

export const MainContext = createContext(null);

export function useMainContext() {
  return useContext(MainContext);
}

export const Loading = HideScreen;

export default function MainContextProvider(props) {
  const { logout } = useSession();
  const loaded = useDataSettings("loaded") || true;
  const [deletingRequest, setDeletingRequest] = useState(false);
  const [unsavedRequest, setUnsavedRequest] = useState(false);
  const [confirmRequest, setConfirmRequest] = useState(false);
  const { pathname } = useLocation();

  const [NotificationContainer, NotificationManager] = useNotifications();

  const requestConfirm = useCallback((message, callback) => {
    setConfirmRequest({ message, callback, show: true });
  }, []);

  const onConfirm = useCallback(() => {
    setConfirmRequest({ show: false });
    confirmRequest.callback(true);
  }, [confirmRequest]);

  const requestDeleteConfirm = useCallback((message, callback) => {
    setDeletingRequest({ message, callback, show: true });
  }, []);

  const requestPromiseDeleteConfirm = useCallback(
    (message) => {
      const { promise, resolve } = PromiseWithResolvers();
      requestDeleteConfirm(message, resolve);
      return promise;
    },
    [requestDeleteConfirm]
  );

  const onConfirmDelete = useCallback(() => {
    deletingRequest.callback(true);
    setDeletingRequest({ show: false });
  }, [deletingRequest]);

  // message: what would be lost (default: the form being edited)
  const requestUnsavedConfirm = useCallback((callback, message) => {
    setUnsavedRequest({ callback, message, show: true });
  }, []);

  const requestPromiseUnsavedConfirm = useCallback(
    (message) => {
      const { promise, resolve } = PromiseWithResolvers();
      requestUnsavedConfirm(resolve, message);
      return promise;
    },
    [requestUnsavedConfirm]
  );

  const onConfirmUnsaved = useCallback(() => {
    unsavedRequest.callback(true);
    setUnsavedRequest({ show: false });
  }, [setUnsavedRequest, unsavedRequest]);

  const createNotification = useCallback(
    (type, message) => {
      switch (type) {
        case "info":
          playSound("done");
          NotificationManager.info(message);
          break;
        case "success":
          playSound("ok");
          NotificationManager.success(message);
          break;
        case "warning":
          playSound("warning");
          NotificationManager.warning(message);
          break;
        case "error":
          playSound("ko");
          NotificationManager.error(message);
          break;
        default:
          throw new Error();
      }
    },
    [NotificationManager]
  );

  const timeoutRef = useRef();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  useTimeout({
    onWarning: useCallback(() => {
      playSound("warning");
      NotificationManager.warning(
        "Idle time exceeded! You are about to be disconnected",
        false
      );
    }, [NotificationManager]),
    onEnded: useCallback(() => logout(), [logout]),
    onRestart: useCallback(
      () => NotificationManager.resetAll(),
      [NotificationManager]
    ),
    onTick: useCallback((obj) => {
      timeoutRef.current = obj;
    }, [])
  });

  const ctx = useMemo(
    () => ({
      ok: () => playSound("ok"),
      ko: () => playSound("ko"),
      done: () => playSound("done"),
      warning: () => playSound("warning"),
      requestConfirm,
      requestDeleteConfirm,
      requestPromiseDeleteConfirm,
      requestUnsavedConfirm,
      requestPromiseUnsavedConfirm,
      createNotification,
      getTimeoutCounter: () => timeoutRef.current
    }),
    [
      requestConfirm,
      requestDeleteConfirm,
      requestPromiseDeleteConfirm,
      requestUnsavedConfirm,
      requestPromiseUnsavedConfirm,
      createNotification
    ]
  );

  return (
    <MainContext.Provider value={ctx}>
      {NotificationContainer}

      <ConfirmGeneric
        onConfirm={onConfirm}
        onClose={() => setConfirmRequest({ show: false })}
        message={confirmRequest.message}
        show={confirmRequest.show}
      />
      <ConfirmDelete
        onConfirm={onConfirmDelete}
        onClose={() => {
          deletingRequest.callback(false);
          setDeletingRequest({ show: false });
        }}
        message={deletingRequest.message}
        show={deletingRequest.show}
      />
      <ConfirmUnsavedChanges
        onConfirm={onConfirmUnsaved}
        onClose={() => {
          unsavedRequest.callback(false);
          setUnsavedRequest({ show: false });
        }}
        message={unsavedRequest.message}
        show={unsavedRequest.show}
      />
      {loaded && props ? (
        props.children
      ) : (
        <HideScreen message="L0AD1NG S3TT1NGS" />
      )}
    </MainContext.Provider>
  );
}

// {
//   saving && <HideScreen message="S4V1NG" />;
// }
// {
//   deleting && <HideScreen message="D3L3T1NG" />;
// }
